/**
 * Timeline Commit Entry
 *
 * @module features/releases/components/TimelineCommitEntry
 * @description Displays a commit in the timeline as a small circle with commit info.
 */

import React from 'react';
import { Text } from '@chatsdet/ui';
import { CheckCircle } from '@phosphor-icons/react';
import { formatDistanceToNow, format } from 'date-fns';
import type { TimelineCommitEntry as CommitEntry } from '../hooks/useReleaseTimeline';
import type { RepoTagInfo } from './ReleaseTimeline';
import { LiveMarker, TimelineNode } from './TimelineNode';
import type { PromotableCommit } from './PromoteCommitDialog';
import { ReleaseRowMenu, type ClaimTarget } from './ReleaseRowMenu';

type Props = {
  entry: CommitEntry;
  deployedSha?: string | null;
  /**
   * When set (multi-repo configs), renders a colored `{label}` mustache tag
   * before the SHA so the source repo is obvious without leaving the row.
   */
  repoTag?: RepoTagInfo | null;
  /**
   * Reports the commit upward to open the Ship commit dialog — passed for
   * every row regardless of whether any `Promote → <stage>` runbook applies
   * to this commit, since the dialog's default action (Run tests) needs no
   * promotion runbook at all. Optional only so a band with no released
   * commit can skip rendering this row's slot entirely; the row reports the
   * commit rather than owning the dialog because a timeline can have dozens
   * of these rows, and mounting one `PromoteCommitDialog` (with its own
   * mutation) per row for a control that can only ever be open once is
   * wasted hooks and re-render surface.
   */
  onPromote?: (commit: PromotableCommit) => void;
  /**
   * Rendered inside a `ReleaseBand` as the run's released commit. Suppresses
   * this row's own leading indicator (the band already shows a single gauge
   * for the pair) and its own author/repo-tag/time/status line — that's
   * duplicated chrome inside a band, which carries all of it on its own
   * single meta line instead. The sha/message line is indented to align
   * under the band's title.
   */
  embedded?: boolean;
  /**
   * Embedded-only: makes the commit message clickable, navigating to the
   * band's run detail page — the band's click-through affordance, scoped to
   * just the message text so it doesn't swallow clicks on the sha link or
   * the ship control beside it.
   */
  onMessageClick?: () => void;
  /** The application's sandbox environments, for the menu's Claim items. Optional only because the embedded call from `TimelineRunEntry` omits it (the band owns the menu). */
  claimTarget?: ClaimTarget | null;
};

export function TimelineCommitEntry({
  entry,
  deployedSha,
  repoTag,
  onPromote,
  embedded,
  onMessageClick,
  claimTarget,
}: Props) {
  const commitUrl = entry.commitUrl;
  const isTested = entry.isTested;
  const isDeployed = deployedSha !== undefined && deployedSha !== null && entry.sha === deployedSha;
  const committedAt = new Date(entry.committedAt);
  const absoluteTimestamp = format(committedAt, "MMM d, yyyy 'at' h:mm a");

  const indicator = (
    <TimelineNode
      suiteRun={entry.suiteRun}
      status={isDeployed ? 'deployed' : isTested ? 'tested' : 'neutral'}
      isDeployed={isDeployed}
      ship={
        onPromote
          ? { commit: { sha: entry.sha, message: entry.message }, onShip: onPromote }
          : undefined
      }
    />
  );

  const row = (
    <div
      className={
        embedded
          ? // The commit IS the band's title now, so this sits flush against
            // the band's own node — same `gap-3` a normal commit row uses —
            // rather than indented under a separate title line.
            'group relative flex items-start gap-3'
          : // A release band's node sits inside a bordered card, so it starts
            // one border plus one `padding="sm"` (p-4) in from the band's outer
            // edge. Matching that inset here puts every node — band and commit
            // alike — on the same vertical line as the rail. Written as a calc
            // of the padding token rather than a pixel literal because the root
            // font size is 14px, so `p-4` is 14px, not 16px.
            `group group/run relative flex items-start gap-3 pl-[calc(1rem+1px)] ${
              isDeployed ? 'pt-1 pb-3' : 'py-3'
            }`
      }
    >
      {/* Circle indicator */}
      {!embedded && indicator}

      {/* Content */}
      <div className="flex-1 min-w-0 pt-1">
        <div className="flex items-baseline gap-2">
          {commitUrl ? (
            <a
              href={commitUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              {entry.shortSha}
            </a>
          ) : (
            <Text size="sm" weight="medium" className="font-mono text-foreground">
              {entry.shortSha}
            </Text>
          )}
          <Text
            size="sm"
            className={`flex-1 min-w-0 truncate ${
              onMessageClick ? 'cursor-pointer text-muted-foreground hover:text-primary-600 transition-colors' : 'text-muted-foreground'
            }`}
            onClick={onMessageClick}
          >
            {entry.message}
          </Text>
        </div>
        {/* Suppressed when embedded in a release band — the band's single
            meta line carries status/repo/deployed/tested for the pair
            instead, so this doesn't repeat it a second time in the band. */}
        {!embedded && (
          <div className="flex items-center gap-1.5 mt-0.5">
            {entry.authorName && (
              <Text size="xs" className="text-muted-foreground">
                {entry.authorName}
              </Text>
            )}
            {repoTag && (
              <Text size="xs" weight="medium" className={`font-mono ${repoTag.textClass}`}>
                {`{${repoTag.label}}`}
              </Text>
            )}
            <span title={absoluteTimestamp} className="cursor-help">
              <Text size="xs" className="text-muted-foreground/70">
                {formatDistanceToNow(committedAt, { addSuffix: true })}
              </Text>
            </span>
            {isTested && (
              <Text size="xs" className="text-green-600 cofactor-dark:text-green-400">
                · Tested
              </Text>
            )}
          </div>
        )}
        {entry.tasks.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {entry.tasks.map((task) => {
              const prUrl =
                entry.repoFullName && task.resolutionPrNumber
                  ? `https://github.com/${entry.repoFullName}/pull/${task.resolutionPrNumber}`
                  : null;
              return (
                <span
                  key={task.id}
                  className="inline-flex min-w-0 max-w-full items-center gap-1 rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-emerald-800 cofactor-dark:border-emerald-800 cofactor-dark:bg-emerald-950 cofactor-dark:text-emerald-200"
                >
                  <CheckCircle size={12} weight="fill" className="flex-shrink-0" />
                  <Text size="xs" weight="medium" className="truncate">
                    {task.title}
                  </Text>
                  {prUrl ? (
                    <a
                      href={prUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-shrink-0 text-xs font-medium text-emerald-700 hover:text-emerald-900 cofactor-dark:text-emerald-300 cofactor-dark:hover:text-emerald-100"
                    >
                      #{task.resolutionPrNumber}
                    </a>
                  ) : task.resolutionPrNumber ? (
                    <Text size="xs" className="flex-shrink-0 text-emerald-700 cofactor-dark:text-emerald-300">
                      #{task.resolutionPrNumber}
                    </Text>
                  ) : null}
                </span>
              );
            })}
          </div>
        )}
      </div>
      {!embedded && claimTarget && (
        <div className="pr-3">
          <ReleaseRowMenu
            claimVersion={{ version: entry.sha, repoFullName: entry.repoFullName }}
            claimTarget={claimTarget}
          />
        </div>
      )}
    </div>
  );

  // Embedded rows don't own their node — the band draws it, so the band also
  // draws the marker above it (see `TimelineRunEntry`).
  if (embedded || !isDeployed) return row;

  return (
    <div>
      {/* Aligned with the commit sha/message column, not the node — the node
          already carries its own "live" halo/ring (see TimelineNode), so a
          marker over the node would be redundant. Same inset as the "Load
          more" button in ReleaseTimeline: node inset (1rem+1px) + node width
          (2rem) + row gap (0.75rem) = 3.75rem+1px, landing on the sha/message
          text rather than the node. */}
      <div className="pl-[calc(3.75rem+1px)] pt-3">
        <LiveMarker />
      </div>
      {row}
    </div>
  );
}
